import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { ethers } from "ethers";
import { goBack } from "../../store/slices/pageSlice";
import Header from "../../components/header/Header";
import BalanceActionsBar from "../../components/BalanceActionsBar";
import ConnectWallet from "../../components/ConnectWallet/ConnectWallet";
import userApi from "../../api/userApi";
import walletIcon from "../../assets/icon/wallet.png";
import getIcon from "../../utils/Icon";

const ERC20_ABI = [
    'function balanceOf(address) view returns (uint256)',
    'function decimals() view returns (uint8)',
];

// RPC через наш HTTPS бэкенд — решает проблему mixed content
const RPC_PROXY = 'https://api.duotetra.quest/promos/blockchain/rpc';

const CHAIN_META = {
    '31337': { name: 'Hardhat Local', color: '#F7C520', short: 'HH'   },
    '1':     { name: 'Ethereum',      color: '#627EEA', short: 'ETH'  },
    '56':    { name: 'BNB Chain',     color: '#F0B90B', short: 'BSC'  },
    '137':   { name: 'Polygon',       color: '#8247E5', short: 'POL'  },
    '42161': { name: 'Arbitrum',      color: '#28A0F0', short: 'ARB'  },
    '8453':  { name: 'Base',          color: '#0052FF', short: 'BASE' },
};

const TOKEN_CONFIG = {
    '31337': [
        { symbol: 'ETH',  name: 'Ethereum',  icon: 'ETH',  native: true },
        { symbol: 'USDT', name: 'Mock USDT', icon: 'USDT', address: '0x5FbDB2315678afecb367f032d93F642f64180aa3' },
    ],
    '1': [
        { symbol: 'ETH',  name: 'Ethereum', icon: 'ETH',  native: true },
        { symbol: 'USDT', name: 'Tether',   icon: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7' },
    ],
    '56': [
        { symbol: 'BNB',  name: 'BNB',    icon: 'BNB',  native: true },
        { symbol: 'USDT', name: 'Tether', icon: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955' },
    ],
};

const short = (a) => a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '';
const fmt   = (v, d = 4) => { const n = parseFloat(v); return isNaN(n) ? '0' : n.toFixed(d).replace(/\.?0+$/, '') || '0'; };

// ─── Icons ────────────────────────────────────────────────────────────────────
const CopyIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const CheckSmall = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const PowerIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>;
const StarIcon   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const PlusIcon   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const RefreshIcon= () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;

// ─── Получаем провайдер ───────────────────────────────────────────────────────
function getProvider() {
    if (typeof window !== 'undefined' && window.ethereum)
        return new ethers.BrowserProvider(window.ethereum);
    // Fallback через HTTPS proxy (работает в Telegram)
    return new ethers.JsonRpcProvider(RPC_PROXY);
}

// ─── Загрузка балансов для одного адреса ─────────────────────────────────────
async function loadBalances(address, chainId) {
    const provider = getProvider();
    const tokens   = TOKEN_CONFIG[String(chainId)] || TOKEN_CONFIG['31337'];
    const result   = {};
    await Promise.all(tokens.map(async (token) => {
        try {
            if (token.native) {
                const raw = await provider.getBalance(address);
                result[token.symbol] = ethers.formatEther(raw);
            } else {
                const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
                const [raw, decimals] = await Promise.all([contract.balanceOf(address), contract.decimals()]);
                result[token.symbol] = ethers.formatUnits(raw, decimals);
            }
        } catch { result[token.symbol] = null; }
    }));
    return result;
}

// ─── Token row ────────────────────────────────────────────────────────────────
function TokenRow({ token, balance, loading }) {
    return (
        <div className="flex items-center justify-between py-2 border-b border-white/[0.05] last:border-0">
            <div className="flex items-center gap-2">
                <img width={18} height={18} src={getIcon(token.icon)} alt={token.symbol} className="object-contain" />
                <span className="text-white/60 text-[12px]">{token.symbol}</span>
            </div>
            {loading
                ? <div className="w-12 h-3 rounded-full bg-white/10 animate-pulse" />
                : <span className="text-white/80 text-[13px] font-semibold tabular-nums">
                    {balance !== null ? fmt(balance) : '—'}
                  </span>
            }
        </div>
    );
}

// ─── Wallet card ──────────────────────────────────────────────────────────────
function WalletCard({ wallet, balances, loading, onRemove, onSetPrimary, onRefresh }) {
    const [copied, setCopied] = useState(false);
    const chainStr = String(wallet.walletChainId || '31337');
    const chain    = CHAIN_META[chainStr] || { name: `Chain ${chainStr}`, color: '#666' };
    const tokens   = TOKEN_CONFIG[chainStr] || TOKEN_CONFIG['31337'];

    const copy = () => {
        navigator.clipboard.writeText(wallet.walletAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className={`w-full rounded-2xl border overflow-hidden ${wallet.isPrimary ? 'border-emerald-500/30' : 'border-white/[0.08]'} bg-white/[0.04]`}>
            {/* Header */}
            <div className="px-4 pt-3 pb-2.5 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span
                        className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
                        style={{ background: chain.color + '22', color: chain.color, border: `1px solid ${chain.color}44` }}
                    >
                        {chain.name}
                    </span>
                    {wallet.isPrimary && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-semibold">
                            <StarIcon /> Primary
                        </span>
                    )}
                    {wallet.label && (
                        <span className="text-[11px] text-white/35">{wallet.label}</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={onRefresh} className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/10 text-white/35 transition active:text-white/70">
                        <RefreshIcon />
                    </button>
                    {!wallet.isPrimary && (
                        <button onClick={onSetPrimary} className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/10 text-white/35 transition active:text-amber-400" title="Set as primary">
                            <StarIcon />
                        </button>
                    )}
                    <button onClick={onRemove} className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/10 text-white/35 transition active:text-rose-400">
                        <PowerIcon />
                    </button>
                </div>
            </div>

            {/* Address */}
            <button onClick={copy} className="w-full flex items-center justify-between px-4 py-2.5 border-b border-white/[0.05] transition active:bg-white/[0.04]">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 flex-shrink-0">
                        <CheckSmall />
                    </div>
                    <span className="font-mono text-[13px] text-white/75">{short(wallet.walletAddress)}</span>
                </div>
                <span className={copied ? 'text-emerald-400' : 'text-white/25'}>
                    {copied ? <CheckSmall /> : <CopyIcon />}
                </span>
            </button>

            {/* Balances */}
            <div className="px-4 py-1">
                {tokens.map(token => (
                    <TokenRow
                        key={token.symbol}
                        token={token}
                        balance={balances?.[token.symbol] ?? null}
                        loading={loading}
                    />
                ))}
            </div>
        </div>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ onConnect }) {
    return (
        <div className="flex flex-col items-center py-14 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center mb-5">
                <img src={walletIcon} width={34} height={34} className="opacity-25" alt="wallet" />
            </div>
            <span className="text-white text-[17px] font-semibold mb-2">No wallets connected</span>
            <span className="text-white/35 text-[13px] leading-relaxed mb-8">
                Connect your EVM wallet to deposit,<br />pay fees and claim prizes
            </span>
            <button
                onClick={onConnect}
                className="flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-[#3478F6] text-white text-[15px] font-semibold transition active:opacity-80"
            >
                <PlusIcon /> Connect Wallet
            </button>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WalletPage() {
    const dispatch = useDispatch();

    const [sheetOpen, setSheetOpen]   = useState(false);
    const [wallets, setWallets]       = useState([]);       // UserWallet[]
    const [balancesMap, setBalancesMap] = useState({});     // { walletId: { ETH: '1.2', ... } }
    const [loadingMap, setLoadingMap] = useState({});       // { walletId: true/false }
    const [pageLoading, setPageLoading] = useState(true);

    const loadWallets = useCallback(async () => {
        setPageLoading(true);
        try {
            const list = await userApi.getWallets();
            setWallets(list || []);
            // Загружаем балансы для каждого кошелька
            list?.forEach(w => fetchBalance(w));
        } catch { setWallets([]); }
        finally { setPageLoading(false); }
    }, []);

    const fetchBalance = useCallback(async (wallet) => {
        setLoadingMap(prev => ({ ...prev, [wallet.id]: true }));
        try {
            const b = await loadBalances(wallet.walletAddress, wallet.walletChainId || '31337');
            setBalancesMap(prev => ({ ...prev, [wallet.id]: b }));
        } catch {}
        finally { setLoadingMap(prev => ({ ...prev, [wallet.id]: false })); }
    }, []);

    useEffect(() => { loadWallets(); }, [loadWallets]);

    const handleRemove = async (walletId) => {
        await userApi.removeWallet(walletId);
        setWallets(prev => prev.filter(w => w.id !== walletId));
    };

    const handleSetPrimary = async (walletId) => {
        await userApi.setPrimaryWallet(walletId);
        setWallets(prev => prev.map(w => ({ ...w, isPrimary: w.id === walletId })));
    };

    const handleConnected = async (addr, chainId) => {
        if (!addr) return;
        setSheetOpen(false);
        await loadWallets();
    };

    return (
        <div className="w-full h-full flex flex-col overflow-hidden">
            <div className="pt-[24px] pb-[10px] flex-shrink-0">
                <Header
                    leftButtonText="Back"
                    centerTitle="My Wallets"
                    onLeftClick={() => dispatch(goBack())}
                    useBackIcon={true}
                />
            </div>

            <div className="flex-1 overflow-y-auto px-4 pt-2 pb-6 space-y-3">
                {pageLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                    </div>
                ) : wallets.length === 0 ? (
                    <EmptyState onConnect={() => setSheetOpen(true)} />
                ) : (
                    <>
                        {wallets.map(wallet => (
                            <WalletCard
                                key={wallet.id}
                                wallet={wallet}
                                balances={balancesMap[wallet.id]}
                                loading={loadingMap[wallet.id]}
                                onRemove={() => handleRemove(wallet.id)}
                                onSetPrimary={() => handleSetPrimary(wallet.id)}
                                onRefresh={() => fetchBalance(wallet)}
                            />
                        ))}

                        {/* Add another wallet */}
                        <button
                            onClick={() => setSheetOpen(true)}
                            className="w-full flex items-center justify-center gap-2 rounded-2xl border border-dashed border-white/[0.15] py-4 text-[14px] text-white/40 transition active:border-white/30 active:text-white/60"
                        >
                            <PlusIcon /> Add another wallet
                        </button>
                    </>
                )}
            </div>

            <BalanceActionsBar />

            <ConnectWallet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                onConnected={handleConnected}
            />
        </div>
    );
}
